/**
 * Activity tab — the lead's events and its texts / Slack posts
 * (`GET /api/leads/:id/messages`) interleaved by time, newest first. Events
 * are timeline rows; messages are chat bubbles (outbound right on navy,
 * inbound left).
 */
import { useMemo } from 'react';
import { Bot, Plug, Settings2, User } from 'lucide-react';
import { fullDateTime, relativeTime } from '@/lib/time';
import type { ActorKind, LeadEvent, Stage } from '@/features/pipeline/types';
import { useSlackChannels } from '@/features/settings/settings-api';
import { mergeActivity } from './activity-merge';
import { ACTOR_LABEL, summarizeEvent } from './event-summary';
import { MessageBubble } from './MessageBubble';
import { useLeadMessages } from './messages-api';
import { useLeadPrompts } from './nudge-api';
import { PromptRow } from './PromptRow';

const ACTOR_ICON: Record<ActorKind, typeof User> = {
  user: User,
  system: Settings2,
  ai: Bot,
  integration: Plug,
};

export function LeadActivityTab({ leadId, events, stages }: { leadId: string; events: LeadEvent[]; stages: Stage[] }) {
  const stagesById = useMemo(() => new Map(stages.map((s) => [s.id, s])), [stages]);
  const messagesQ = useLeadMessages(leadId);
  const promptsQ = useLeadPrompts(leadId);
  const items = useMemo(
    () => mergeActivity(events, messagesQ.data ?? [], promptsQ.data ?? []),
    [events, messagesQ.data, promptsQ.data],
  );
  // Stage 6: only ask Innu for channel names when a Slack post is actually on the timeline.
  const hasSlack = useMemo(() => (messagesQ.data ?? []).some((m) => m.channel === 'slack'), [messagesQ.data]);
  const channelsQ = useSlackChannels(hasSlack);
  const slackChannelName = useMemo(() => {
    const byId = new Map((channelsQ.data ?? []).map((c) => [c.id, `#${c.name}`]));
    return (id: string) => byId.get(id);
  }, [channelsQ.data]);

  if (items.length === 0) {
    return (
      <p className="px-1 py-6 text-center text-sm text-muted">
        {messagesQ.isPending || promptsQ.isPending ? 'Loading activity…' : 'Nothing has happened yet.'}
      </p>
    );
  }

  return (
    <>
      {(messagesQ.isError || promptsQ.isError) && (
        <p role="alert" className="mb-3 rounded-md bg-danger-bg px-3 py-2 text-[12px] text-danger">
          {messagesQ.isError ? 'Texts' : 'Nudges'} could not be loaded; showing the rest.
        </p>
      )}
      <ol className="relative ml-3 border-l border-gray-200 pl-4" aria-label="Activity">
        {items.map((item) => {
          if (item.kind === 'prompt') {
            return (
              <li key={`p-${item.prompt.id}`} className="relative pb-4 last:pb-0">
                <span className="absolute -left-[21px] top-2 h-2.5 w-2.5 rounded-full border-2 border-white bg-gray-300" aria-hidden />
                <PromptRow prompt={item.prompt} />
              </li>
            );
          }
          if (item.kind === 'message') {
            return (
              <li key={`m-${item.message.id}`} className="relative pb-4 last:pb-0">
                <span className="absolute -left-[21px] top-2 h-2.5 w-2.5 rounded-full border-2 border-white bg-gray-300" aria-hidden />
                <MessageBubble message={item.message} slackChannelName={slackChannelName} />
              </li>
            );
          }
          const { event } = item;
          const Icon = ACTOR_ICON[event.actor_kind] ?? User;
          return (
            <li key={`e-${event.id}`} className="relative pb-4 last:pb-0">
              <span className="absolute -left-[25px] top-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white ring-1 ring-gray-200">
                <Icon size={11} strokeWidth={2} className="text-gray-500" aria-hidden />
              </span>
              <div className="text-[13px] leading-5 text-gray-900">{summarizeEvent(event, stagesById)}</div>
              <div className="mt-0.5 text-[12px] text-muted">
                {ACTOR_LABEL[event.actor_kind] ?? event.actor_kind}
                <span aria-hidden> · </span>
                <time dateTime={event.created_at} title={fullDateTime(event.created_at)}>
                  {relativeTime(event.created_at)}
                </time>
              </div>
            </li>
          );
        })}
      </ol>
    </>
  );
}
